import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { Button, Stack, Typography } from "@mui/material";
import { ContentCard } from "@shared";

import { useTranslation } from "@/translations";

interface Props {
  isLocked: boolean;
  onLock: () => void;
  onUnlock: () => void;
  onSendPasswordReset: () => void;
  /** Present for client subjects only; the button is not rendered otherwise. */
  onRemoveUser?: () => void;
  isBusy: boolean;
}

/**
 * The "Zugang verwalten" card once the subject has registered: lock/unlock the portal login,
 * trigger the password reset, and — where offered — remove the portal user. Removing stays
 * available while the user is locked: a blocked access is exactly the one that most often has to
 * be cleaned up.
 */
export function PortalManageAccessCard({
  isLocked,
  onLock,
  onUnlock,
  onSendPasswordReset,
  onRemoveUser,
  isBusy,
}: Props) {
  const { t } = useTranslation();

  return (
    <ContentCard title={t("portalAccess.manage.title")} grow={false}>
      <Stack spacing={2} alignItems="flex-start">
        <Stack direction="row" spacing={2}>
          {isLocked ? (
            <Button variant="outlined" startIcon={<LockOpenOutlinedIcon />} onClick={onUnlock} disabled={isBusy}>
              {t("portalAccess.manage.unlock")}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockOutlinedIcon />}
              onClick={onLock}
              disabled={isBusy}
            >
              {t("portalAccess.manage.lock")}
            </Button>
          )}

          {onRemoveUser && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<PersonRemoveOutlinedIcon />}
              onClick={onRemoveUser}
              disabled={isBusy}
            >
              {t("portalAccess.manage.removeUser")}
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<LockResetOutlinedIcon />}
            onClick={onSendPasswordReset}
            disabled={isBusy || isLocked}
          >
            {t("portalAccess.manage.resetPassword")}
          </Button>
        </Stack>

        {isLocked && (
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon color="error" fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              {t("portalAccess.manage.lockedInfo")}
            </Typography>
          </Stack>
        )}
      </Stack>
    </ContentCard>
  );
}
